import { Provider, ProviderToken, Type } from '@angular/core';

/**
 * Constructor signature of every generated API class — `new ApiClass(config: TConfig)`.
 * Both the constructor itself and the resulting instance act as the Angular DI token, so
 * consumers `inject(UsersApi)` and the lib registers `{ provide: UsersApi, useFactory, deps }`.
 */
export type ApiClientClass<TConfig> = Type<unknown> & (new (config: TConfig) => unknown);

export interface ApiClientGroupOptions<TConfig> {
  /**
   * The generated Configuration class for this client group (`PublicV2Configuration`,
   * `FrontendV2Configuration`, `AdminV1Configuration`). Also acts as the DI token consumers
   * inject when they need the raw Configuration.
   */
  readonly configurationToken: ProviderToken<TConfig>;

  /** Factory that builds the Configuration instance — apps wire `basePath` + `middleware` here. */
   
  readonly configurationFactory: (...deps: any[]) => TConfig;

  /** Angular DI tokens passed to `configurationFactory`. */
  readonly configurationDeps?: ProviderToken<unknown>[];

  /**
   * Generated API classes to wire into DI. Each entry registers `{ provide: ApiClass,
   * useFactory: c => new ApiClass(c), deps: [configurationToken] }`, collapsing the
   * dozens of repetitive blocks app bootstraps would otherwise have.
   */
  readonly apis: ReadonlyArray<ApiClientClass<TConfig>>;
}

/**
 * Builds Angular providers for one generated API client group: the Configuration provider
 * plus one provider per API class that depends on it. The result is a flat `Provider[]`
 * that callers spread into their `bootstrapApplication` / `appConfig` providers list.
 */
export function provideApiClientGroup<TConfig>(
  options: ApiClientGroupOptions<TConfig>
): Provider[] {
  const providers: Provider[] = [
    {
      provide: options.configurationToken,
      useFactory: options.configurationFactory,
      deps: options.configurationDeps ?? [],
    },
  ];

  for (const ApiClass of options.apis) {
    providers.push({
      provide: ApiClass,
      useFactory: (config: TConfig) => new ApiClass(config),
      deps: [options.configurationToken],
    });
  }

  return providers;
}
